import { useCallback, useRef } from "react";

import {
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { ExportReportParamsObjectTypeEnum } from "api";
import { useRequestParams } from "contexts/RequestParamsProvider";
import useGetFormattedCsv from "hooks/api/documents/useGetFormattedCsv";
import useSelectedCustomerCode from "hooks/useSelectedCustomerCode";
import { ChevronDownIcon } from "icons";
import { createFileDownload } from "util/downloadBlob";
import EmailCsvModal from "./EmailCsvModal.tsx";

const downloadCsvBlob = (data: string, filename: string) => {
  const file = new Blob([data], { type: "text/csv" });
  createFileDownload(file, filename);
};

const ExportButton = ({
  documentType = "documents",
  objectType,
}: {
  documentType: string | undefined;
  objectType: ExportReportParamsObjectTypeEnum;
}) => {
  const {
    isOpen: isEmailModalOpen,
    onOpen,
    onClose: onCloseEmailModal,
  } = useDisclosure();
  const selectedCustomerCodes = useSelectedCustomerCode();
  const { filters, searchTerm } = useRequestParams();

  const { mutate: downloadCsv, isLoading } = useGetFormattedCsv(
    selectedCustomerCodes
  );

  const onDownloadCsv = useCallback(() => {
    downloadCsv(
      {
        cardCodes: selectedCustomerCodes,
        objectType,
        searchString: searchTerm || undefined,
        ...filters,
      },
      {
        onSuccess: (data) => {
          if (data) {
            const timestamp = new Date().toISOString();
            downloadCsvBlob(
              data,
              `IRM Customer Portal - ${documentType}-${timestamp}.csv`
            );
          }
        },
      }
    );
  }, [
    documentType,
    downloadCsv,
    filters,
    objectType,
    searchTerm,
    selectedCustomerCodes,
  ]);

  const menuListRef = useRef(null);

  return (
    // Intentionally setting initialFocusRef to null so Chakra doesn't try to focus the first item in the menu
    <Menu placement="bottom-end" initialFocusRef={menuListRef}>
      <MenuButton as={Button} variant="solid" backgroundColor="navy.600">
        <HStack spacing={2}>
          <Text color="white">Export</Text>
          <Spacer />
          <ChevronDownIcon color="white" />
        </HStack>
      </MenuButton>
      <MenuList
        data-focus-visible-disabled
        borderColor="grey.100"
        boxShadow="2xl"
        minW="160px"
      >
        <MenuItem
          isDisabled={isLoading}
          onClick={onDownloadCsv}
          color="grey.600"
          fontWeight="black"
          fontSize="sm"
          _hover={{ bg: "navy.50" }}
          isFocusable
        >
          Download .CSV
        </MenuItem>
        <MenuItem
          isDisabled={isLoading}
          onClick={onOpen}
          color="grey.600"
          fontWeight="black"
          fontSize="sm"
          _hover={{ bg: "navy.50" }}
        >
          Email .CSV
        </MenuItem>
        <EmailCsvModal
          objectType={objectType}
          searchString={searchTerm}
          filters={filters}
          isOpen={isEmailModalOpen}
          onClose={onCloseEmailModal}
        />
      </MenuList>
    </Menu>
  );
};

export default ExportButton;
